/**
 * Service pour la gestion des emails cPanel
 * Utilise l'Edge Function cpanel-email-manager comme proxy sécurisé
 */

import { supabase } from '../lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export interface CpanelEmail {
  email: string;
  user: string;
  domain: string;
  diskquota: string | number;
  diskused: number;
  _diskquota: number;
  _diskused: number;
  diskusedpercent: number;
  diskusedpercent_float: number;
  humandiskquota: string;
  humandiskused: string;
  suspended_incoming: number;
  suspended_login: number;
  suspended_outgoing: number;
  has_suspended: number;
  hold_outgoing: number;
  login: string;
  mtime: number;
  txtdiskquota: string;
}

export interface CreateEmailParams {
  email: string;
  password: string;
  domain: string;
  quota?: number | 'unlimited';
}

export interface UpdatePasswordParams {
  email: string;
  password: string;
  domain: string;
}

export interface DeleteEmailParams {
  email: string;
  domain: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/**
 * Appelle l'Edge Function cpanel-email-manager
 */
async function callEdgeFunction(payload: Record<string, any>): Promise<ApiResponse> {
  try {
    // Récupérer le token de session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      return { success: false, error: 'Non authentifié' };
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/cpanel-email-manager`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      return { 
        success: false, 
        error: data.error || `Erreur ${response.status}` 
      };
    }

    return data;
  } catch (error: any) {
    console.error('[cpanelEmailService] Error:', error);
    return { 
      success: false, 
      error: error.message || 'Erreur de connexion' 
    };
  }
}

/**
 * Liste les domaines disponibles dans cPanel (filtrés selon les permissions de l'utilisateur)
 */
export async function listDomains(): Promise<ApiResponse<string[]>> {
  console.log('[cpanelEmailService] Listing available domains');
  
  // Envoyer le domaine actuel pour le fallback
  const currentDomain = window.location.hostname;
  
  const result = await callEdgeFunction({
    action: 'list_domains',
    domain: 'any', // Requis par l'API mais ignoré pour cette action
    requestDomain: currentDomain, // Pour le fallback si pas de domaines configurés
  });

  console.log('[cpanelEmailService] list_domains result:', result);

  if (result.success && result.data) {
    // Extraire tous les domaines (main_domain, addon_domains, parked_domains)
    const domains: string[] = [];
    const data = result.data;
    
    console.log('[cpanelEmailService] Domain data structure:', data);
    
    // Domaine principal (si accessible)
    if (data.main_domain) domains.push(data.main_domain);
    
    // Domaines addon (filtrés par permissions)
    if (Array.isArray(data.addon_domains)) domains.push(...data.addon_domains);
    
    // Domaines parqués (alias)
    if (Array.isArray(data.parked_domains)) domains.push(...data.parked_domains);
    
    console.log('[cpanelEmailService] Extracted domains:', domains);
    
    // Si aucun domaine retourné mais le domaine actuel est valide, l'utiliser
    if (domains.length === 0 && currentDomain && currentDomain !== 'localhost') {
      domains.push(currentDomain);
    }
    
    return { success: true, data: domains };
  }

  return result;
}

/**
 * Liste les emails d'un domaine
 */
export async function listEmails(domain: string): Promise<ApiResponse<CpanelEmail[]>> {
  console.log('[cpanelEmailService] Listing emails for domain:', domain);
  
  const result = await callEdgeFunction({
    action: 'list',
    domain,
  });

  if (result.success && result.data) {
    // Filtrer pour ne garder que les emails du domaine demandé
    const emails = Array.isArray(result.data) 
      ? result.data.filter((e: CpanelEmail) => e.domain === domain)
      : [];
    return { success: true, data: emails };
  }

  return result;
}

/**
 * Crée une nouvelle adresse email
 */
export async function createEmail(params: CreateEmailParams): Promise<ApiResponse> {
  console.log('[cpanelEmailService] Creating email:', params.email);
  
  // Valider le format de l'email
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(params.email)) {
    return { success: false, error: 'Format d\'email invalide' };
  }

  // Vérifier que l'email appartient au bon domaine
  const emailDomain = params.email.split('@')[1];
  if (emailDomain !== params.domain) {
    return { success: false, error: 'L\'email doit appartenir au domaine sélectionné' };
  }

  // Valider le mot de passe (minimum 8 caractères)
  if (params.password.length < 8) {
    return { success: false, error: 'Le mot de passe doit contenir au moins 8 caractères' };
  }

  return callEdgeFunction({
    action: 'create',
    domain: params.domain,
    email: params.email,
    password: params.password,
    quota: params.quota === 'unlimited' ? 0 : params.quota,
  });
}

/**
 * Change le mot de passe d'une adresse email
 */
export async function updatePassword(params: UpdatePasswordParams): Promise<ApiResponse> {
  console.log('[cpanelEmailService] Updating password for:', params.email);
  
  // Valider le mot de passe (minimum 8 caractères)
  if (params.password.length < 8) {
    return { success: false, error: 'Le mot de passe doit contenir au moins 8 caractères' };
  }

  return callEdgeFunction({
    action: 'update_password',
    domain: params.domain,
    email: params.email,
    password: params.password,
  });
}

/**
 * Supprime une adresse email
 */
export async function deleteEmail(params: DeleteEmailParams): Promise<ApiResponse> {
  console.log('[cpanelEmailService] Deleting email:', params.email);
  
  return callEdgeFunction({
    action: 'delete',
    domain: params.domain,
    email: params.email,
  });
}

/**
 * Formate la taille en octets en format lisible
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Calcule le pourcentage d'utilisation du quota
 */
export function calculateUsagePercent(used: number, quota: number): number {
  if (quota === 0) return 0; // Quota illimité
  return Math.min(100, Math.round((used / quota) * 100));
}

/**
 * Génère un mot de passe aléatoire sécurisé
 */
export function generateSecurePassword(length: number = 16): string {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  
  const allChars = lowercase + uppercase + numbers + symbols;
  
  let password = '';
  
  // Assurer au moins un caractère de chaque type
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];
  
  // Remplir le reste
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Mélanger le mot de passe
  return password.split('').sort(() => Math.random() - 0.5).join('');
}

/**
 * Retourne l'URL du webmail pour un domaine
 */
export function getWebmailUrl(domain: string): string {
  return `https://${domain}:2096`;
}
